import { MARKET_TIMEZONE } from '@/features/bidding/constants'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { AggregatedBid } from '@/features/bidding/utils/model/buildAggregatedBid'
import { toLuxonDateTime } from '@/features/bidding/utils/model/modelConversion'

type ApiPriceAllocationRequest = {
  aggregatedPricedVolumes: {
    ptus: Array<{
      ptu: {
        start: string
        end: string
      }
      ptuChunks: Array<{
        offeredVolume: {
          quantity: number
          unit: string
        }
        offeredPrice: number
      }>
    }>
  }
  ladderType: 'INCREMENTAL' | 'CUMULATIVE'
  activationGroupAvailableVolumes: Array<{
    activationGroupUuid: string
    ptus: Array<{
      ptu: {
        start: string
        end: string
      }
      volume: {
        quantity: number
        unit: string
      }
    }>
  }>
}

type ApiPriceAllocationResponse = {
  activationGroupPricedOffers: Array<{
    activationGroupUuid: string
    ptus: Array<{
      ptu: {
        start: string
        end: string
      }
      ptuChunks: Array<{
        offeredVolume: {
          quantity: number
          unit: string
        }
        offeredPrice: number
      }>
    }>
  }>
}

export const buildPriceAllocationRequest = (
  aggregatedBid: AggregatedBid,
  activationGroupBids: ExtendedBid[],
): ApiPriceAllocationRequest => {
  return {
    aggregatedPricedVolumes: {
      ptus: aggregatedBid.ptus.map((ptu) => {
        return {
          ptu: {
            start: ptu.ptuStart.toISO()!,
            end: ptu.ptuEnd.toISO()!,
          },
          ptuChunks: ptu.priceQuantities.map((priceQuantity) => ({
            offeredVolume: {
              quantity: Math.round(priceQuantity.quantity * 1_000_000), // Convert MW to Watts
              unit: 'WATTS',
            },
            offeredPrice: priceQuantity.price,
          })),
        }
      }),
    },
    ladderType: aggregatedBid.ladderType === 0 ? 'CUMULATIVE' : 'INCREMENTAL',
    activationGroupAvailableVolumes: activationGroupBids.map((bid) => ({
      activationGroupUuid: bid.activationGroupUuid,
      ptus: bid.ptus.map((ptu) => ({
        ptu: {
          start: ptu.ptu.start.toISO()!,
          end: ptu.ptu.end.toISO()!,
        },
        volume: {
          quantity: ptu.offeredVolume.quantity,
          unit: ptu.offeredVolume.unit,
        },
      })),
    })),
  }
}

export const toPriceAllocationResponse = (
  response: ApiPriceAllocationResponse,
  activationGroupBids: ExtendedBid[],
): ExtendedBid[] => {
  return response.activationGroupPricedOffers.map((offer) => {
    const originalBid = activationGroupBids.find((bid) => bid.activationGroupUuid === offer.activationGroupUuid)!

    return {
      // Use original bid as basis and override only the priced volumes (PTU chunks)
      // This will then for example preserve metadata
      ...originalBid,
      ptus: offer.ptus.map((ptu) => {
        const originalPtu = originalBid.ptus.find((p) =>
          p.ptu.start.equals(toLuxonDateTime(ptu.ptu.start, MARKET_TIMEZONE)),
        )!

        return {
          // Use original PTU to preserve volumes
          ...originalPtu,
          ptuChunks: ptu.ptuChunks.map((chunk) => ({
            offeredVolume: chunk.offeredVolume,
            offeredPrice: chunk.offeredPrice,
          })),
        }
      }),
    }
  })
}
